import { amountsEqual } from '@/lib/format';
import type { BetSummary } from '@/lib/bet';

/**
 * The comparison model — pure, so it can be unit-tested without React.
 *
 * A facet is one attribute of a bet with its A and B values and whether they
 * agree. `context` facets (bet id, player) are identity, not health: two
 * different bets differ there by definition, so they are shown but never flagged
 * as a "difference" and never counted in the differences filter.
 */

export type FacetKind = 'text' | 'id' | 'num' | 'money' | 'outcome' | 'duration';

export interface Facet {
  key: string;
  label: string;
  term?: string;
  kind: FacetKind;
  a: string | number | null;
  b: string | number | null;
  same: boolean;
  context?: boolean;
}

function equal(kind: FacetKind, a: Facet['a'], b: Facet['b']): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (kind === 'money') return amountsEqual(a, b);
  if (kind === 'num' || kind === 'duration') return Number(a) === Number(b);
  return String(a) === String(b);
}

export function compareFacets(
  a: BetSummary,
  b: BetSummary,
  rtpA: string | null,
  rtpB: string | null,
): Facet[] {
  const f = (
    key: string,
    label: string,
    kind: FacetKind,
    av: Facet['a'],
    bv: Facet['b'],
    opts: { term?: string; context?: boolean } = {},
  ): Facet => ({ key, label, kind, a: av, b: bv, same: equal(kind, av, bv), ...opts });

  return [
    f('bet', 'bet id', 'id', a.betId, b.betId, { term: 'bet_id', context: true }),
    f('game', 'game', 'text', a.gameName, b.gameName),
    f('gameType', 'game type', 'text', a.gameType, b.gameType),
    f('operator', 'operator', 'id', a.playServiceId, b.playServiceId, { term: 'play_service_id' }),
    f('currency', 'currency', 'text', a.currency, b.currency),
    f('player', 'player', 'id', a.player, b.player, { context: true }),
    f('outcome', 'outcome', 'outcome', a.outcome, b.outcome),
    f('stake', 'stake', 'money', a.stake, b.stake),
    f('payout', 'payout', 'money', a.payout, b.payout),
    f('net', 'net', 'money', a.net, b.net),
    f('tx', 'transactions', 'num', a.txCount, b.txCount, { term: 'source_transactions' }),
    f('rollbacks', 'rollbacks', 'num', a.rollbacks, b.rollbacks),
    f('sessions', 'sessions', 'num', a.sessions, b.sessions, { term: 'source_sessions' }),
    f('rtp', 'rtp variation', 'text', rtpA, rtpB, { term: 'source_rtpVariation' }),
    f('duration', 'duration', 'duration', a.spanMs, b.spanMs, { term: 'timezone_skew' }),
  ];
}

/** Facets that genuinely differ (identity facets excluded). */
export function differingFacets(facets: Facet[]): Facet[] {
  return facets.filter((x) => !x.same && !x.context);
}
